/**
 * Service-worker cache names — the single source of truth shared by the
 * Workbox `runtimeCaching` config (vite.config.ts) and the app code that
 * purges/cleans those caches.
 *
 * ⚠️ These strings are duplicated in vite.config.ts because the Workbox config
 * is evaluated by the build, outside the app bundle. Keep them in step.
 */

/**
 * Cached API reads (NetworkFirst). Bump the `-vN` suffix whenever the shape of
 * a cached response changes; {@link RUNTIME_CACHE_FAMILIES} then makes the next
 * boot delete the superseded cache.
 */
export const API_CACHE = 'syntry-api-v1';

/**
 * Reserved for avatar/attachment caching, which is deliberately NOT enabled
 * yet (opaque cross-origin responses carry a ~7 MB quota penalty each — see
 * the note in vite.config.ts). Listed in {@link USER_RUNTIME_CACHES} already so
 * enabling it later needs no change to the sign-out purge.
 */
export const MEDIA_CACHE = 'syntry-media-v1';

/**
 * Every runtime cache the CURRENT build owns.
 *
 * `MEDIA_CACHE` is intentionally absent: the media route is disabled, so a
 * media cache found at boot is a leftover and should be cleaned up.
 */
export const EXPECTED_RUNTIME_CACHES: readonly string[] = [API_CACHE];

/**
 * Name prefixes of the runtime caches this app manages, used to find versions
 * left behind by a superseded build.
 *
 * ⚠️ This must stay narrow. Workbox derives its PRECACHE name from the
 * `cacheId` option, so the precache is called `syntry-precache-v2-<scope>` —
 * matching on the bare `syntry-` prefix would delete the precached app shell on
 * every boot and silently destroy offline support.
 */
export const RUNTIME_CACHE_FAMILIES: readonly string[] = ['syntry-api-', 'syntry-media-'];

/**
 * Caches holding data scoped to the signed-in user. Purged at BOTH auth
 * boundaries: `Cache.match()` ignores request headers, and every
 * `/v1/api/me/*` URL is identical across users *and* tenants (the tenant is the
 * `X-Company-Key` header), so without this a second user on a shared device
 * could be served the first user's data.
 */
export const USER_RUNTIME_CACHES: readonly string[] = [API_CACHE, MEDIA_CACHE];
